import React, { useState, useEffect } from 'react';
import { Character } from '../types/character';
import { X, Search, Shield, Lock, Users } from 'lucide-react';
import { getUserCharacters } from '../lib/characters';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from './LoadingSpinner';
import CharacterPreview from './CharacterPreview';

interface Props {
  onClose: () => void;
}

export default function UserCharacters({ onClose }: Props) {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const loadCharacters = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setCurrentUserId(user?.id || null);

        const userCharacters = await getUserCharacters();
        setCharacters(userCharacters);
      } catch (err) {
        console.error('Error loading characters:', err);
        setError('Erreur lors du chargement des personnages');
      } finally {
        setIsLoading(false);
      }
    };

    loadCharacters();
  }, []);

  const getOwnershipStatus = (character: Character) => {
    if (!character.owner_id) return 'Propriétaire';
    if (character.owner_id === currentUserId) return 'Propriétaire';
    return 'Lecture seule';
  };

  const filteredCharacters = characters.filter(char => 
    char.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (selectedCharacter) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
        <CharacterPreview
          character={selectedCharacter}
          onClose={() => setSelectedCharacter(null)}
          readOnly
          background="bg-transparent"
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Mes personnages</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* Barre de recherche */}
          <div className="relative mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un personnage..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              {error}
            </div>
          ) : filteredCharacters.length === 0 ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-3">
                <Users className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                Aucun personnage trouvé
              </h3>
              <p className="text-gray-500 text-sm">
                {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Vous n\'avez pas encore créé de personnage'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredCharacters.map((character) => (
                <button
                  key={character.id}
                  onClick={() => setSelectedCharacter(character)}
                  className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                >
                  {/* Image de profil */}
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                    {character.profileImage ? (
                      <img
                        src={character.profileImage}
                        alt={character.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl font-bold text-gray-400">
                        {character.name.charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Informations */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {character.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {getOwnershipStatus(character) === 'Propriétaire' ? (
                        <div className="flex items-center gap-1 text-green-600 text-sm">
                          <Shield className="w-4 h-4" />
                          <span>Propriétaire</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-600 text-sm">
                          <Lock className="w-4 h-4" />
                          <span>Lecture seule</span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}